/**
 * Reverse Text Tool
 *
 * Reverses text with options: reverse characters, reverse words, or reverse lines.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const ReverseTextTool: ITransformerTool = {
  id: 'reverse-text',
  name: 'Reverse Text',
  description: 'Reverse text by characters, words, or lines',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['reverse', 'text', 'flip', 'mirror', 'backwards', 'invert'],

  seo: {
    slug: 'reverse-text',
    pageTitle: 'Reverse Text - Free Online Tool | Prism',
    metaDescription: 'Reverse text by characters, words, or lines instantly. Unicode-safe character reversal. Fast, private, runs entirely in your browser.',
    keywords: ['reverse text', 'reverse text online', 'flip text', 'reverse words', 'reverse lines', 'backwards text'],
    relatedTools: ['sort-lines', 'case-converter', 'deduplicate-lines'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'mode',
      type: 'select',
      label: 'Reverse Mode',
      defaultValue: 'characters',
      options: ['characters', 'words', 'lines'],
      description: 'What to reverse: characters within each line, word order, or line order',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const mode = (config.mode as string) || 'characters';

    switch (mode) {
      case 'characters':
        // Unicode-safe: use Array.from to handle surrogate pairs
        return input
          .split('\n')
          .map((line) => Array.from(line).reverse().join(''))
          .join('\n');

      case 'words':
        return input
          .split('\n')
          .map((line) => line.split(/(\s+)/).reverse().join(''))
          .join('\n');

      case 'lines':
        return input.split('\n').reverse().join('\n');

      default:
        return input;
    }
  },
};
